import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const { LuZap, LuSearch, LuX, LuCheck, LuPlus, LuMinus, LuSave, LuTrendingUp } = LuIcons;

const FIELD_OPTIONS = [
  { val: 'shipping_price', label: 'سعر الشحن', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-300' },
  { val: 'customs_price', label: 'سعر الجمركة', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-300' },
  { val: 'price', label: 'سعر السيارة', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-300' },
  { val: 'commission', label: 'عمولة المسوق المباشر', color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-300' },
  { val: 'parent_commission', label: 'عمولة المسوق الداعي', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-300' },
];

const OP_OPTIONS = [
  { val: 'add', label: 'إضافة مبلغ', icon: LuPlus },
  { val: 'subtract', label: 'طرح مبلغ', icon: LuMinus },
  { val: 'set', label: 'تعيين قيمة محددة', icon: LuSave },
];

const AdminBulkPriceUpdate = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [field, setField] = useState('shipping_price');
  const [operation, setOperation] = useState('add');
  const [amount, setAmount] = useState('');
  const [updateTotal, setUpdateTotal] = useState(true);

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    setLoading(true);
    const { data } = await supabase.from('cars_for_sale')
      .select('id, brand, model, year, price, shipping_price, customs_price, total_price, commission, parent_commission, status')
      .order('brand');
    setCars(data || []);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (!search) return cars;
    const q = search.toLowerCase();
    return cars.filter(c => c.brand.toLowerCase().includes(q) || c.model.toLowerCase().includes(q));
  }, [cars, search]);

  const allSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.add(c.id));
        return next;
      });
    }
  };

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const computeNewValue = (car, fieldKey, op, amt) => {
    const current = Number(car[fieldKey]) || 0;
    const n = Number(amt) || 0;
    if (op === 'add') return current + n;
    if (op === 'subtract') return Math.max(0, current - n);
    if (op === 'set') return n;
    return current;
  };

  const preview = useMemo(() => {
    if (!amount || selectedIds.size === 0) return null;
    return cars.filter(c => selectedIds.has(c.id)).map(car => {
      const newVal = computeNewValue(car, field, operation, amount);
      const newPrice = field === 'price' ? newVal : (Number(car.price) || 0);
      const newShipping = field === 'shipping_price' ? newVal : (Number(car.shipping_price) || 0);
      const newCustoms = field === 'customs_price' ? newVal : (Number(car.customs_price) || 0);
      const newTotal = newPrice + newShipping + newCustoms;
      return { ...car, newVal, newTotal };
    });
  }, [cars, selectedIds, field, operation, amount]);

  const handleApply = async () => {
    if (!amount || selectedIds.size === 0) return;
    setSaving(true);
    try {
      const ids = [...selectedIds];
      for (const id of ids) {
        const car = cars.find(c => c.id === id);
        if (!car) continue;

        const newVal = computeNewValue(car, field, operation, amount);
        const updates = { [field]: newVal };

        // تحديث السعر الإجمالي فقط إذا لم يكن الحقل هو أحد العمولات
        if (updateTotal && field !== 'commission' && field !== 'parent_commission') {
          const p = field === 'price' ? newVal : (Number(car.price) || 0);
          const s = field === 'shipping_price' ? newVal : (Number(car.shipping_price) || 0);
          const cu = field === 'customs_price' ? newVal : (Number(car.customs_price) || 0);
          updates.total_price = p + s + cu;
        }

        await supabase.from('cars_for_sale').update(updates).eq('id', id);
      }
      setDone(true);
      setTimeout(() => setDone(false), 3000);
      await fetchCars();
      setSelectedIds(new Set());
      setAmount('');
    } catch (err) {
      alert(`حدث خطأ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const selectedField = FIELD_OPTIONS.find(f => f.val === field);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <SafeIcon icon={LuZap} className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-white">تحديث الأسعار الجماعي</h2>
            <p className="text-violet-200 text-xs">عدّل أسعار وعمولات عدة سيارات دفعة واحدة بسرعة</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Step 1: Choose field */}
        <div>
          <p className="text-xs font-black text-slate-500 uppercase mb-3 flex items-center gap-1.5">
            <span className="w-5 h-5 bg-violet-600 text-white rounded-full flex items-center justify-center text-[10px] font-black">1</span>
            اختر الحقل الذي تريد تعديله
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {FIELD_OPTIONS.map(f => (
              <button
                key={f.val}
                onClick={() => setField(f.val)}
                className={`p-3 rounded-xl border-2 font-bold text-sm transition-all ${field === f.val ? `${f.border} ${f.bg} ${f.color}` : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Operation + Amount */}
        <div>
          <p className="text-xs font-black text-slate-500 uppercase mb-3 flex items-center gap-1.5">
            <span className="w-5 h-5 bg-violet-600 text-white rounded-full flex items-center justify-center text-[10px] font-black">2</span>
            العملية والمبلغ
          </p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {OP_OPTIONS.map(op => (
              <button
                key={op.val}
                onClick={() => setOperation(op.val)}
                className={`flex items-center justify-center gap-1.5 p-2.5 rounded-xl border-2 font-bold text-xs transition-all ${operation === op.val ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
              >
                <SafeIcon icon={op.icon} className="w-3.5 h-3.5" /> {op.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="أدخل المبلغ بالدينار الجزائري..."
              className="w-full p-3 pe-16 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none font-bold text-lg"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">دج</span>
          </div>

          {amount && (
            <div className="mt-2 flex items-center gap-2 text-sm font-bold">
              <span className="text-slate-500">سيتم:</span>
              <span className={`${selectedField?.color} ${selectedField?.bg} px-2 py-0.5 rounded-lg`}>
                {operation === 'add' && `إضافة ${Number(amount).toLocaleString()} دج`}
                {operation === 'subtract' && `طرح ${Number(amount).toLocaleString()} دج`}
                {operation === 'set' && `تعيين ${Number(amount).toLocaleString()} دج`}
              </span>
              <span className="text-slate-500">على</span>
              <span className={`${selectedField?.color} font-black`}>{selectedField?.label}</span>
            </div>
          )}

          {field !== 'commission' && field !== 'parent_commission' && (
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${updateTotal ? 'bg-violet-600 border-violet-600' : 'border-slate-300'}`}>
                {updateTotal && <SafeIcon icon={LuCheck} className="w-3 h-3 text-white" />}
              </div>
              <span className="text-sm font-bold text-slate-700">تحديث السعر الإجمالي تلقائياً</span>
            </label>
          )}
        </div>

        {/* Step 3: Select cars */}
        <div>
          <p className="text-xs font-black text-slate-500 uppercase mb-3 flex items-center gap-1.5">
            <span className="w-5 h-5 bg-violet-600 text-white rounded-full flex items-center justify-center text-[10px] font-black">3</span>
            اختر السيارات ({selectedIds.size} محدد من {cars.length})
          </p>
          <div className="relative mb-3">
            <SafeIcon icon={LuSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث بالماركة أو الموديل..."
              className="w-full pe-10 ps-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <SafeIcon icon={LuX} className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden">
            {/* Select all row */}
            <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
              <button
                onClick={toggleAll}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${allSelected ? 'bg-violet-600 border-violet-600' : 'border-slate-300 hover:border-violet-400'}`}
              >
                {allSelected && <SafeIcon icon={LuCheck} className="w-3 h-3 text-white" />}
              </button>
              <span className="text-xs font-black text-slate-600">
                {allSelected ? 'إلغاء تحديد الكل' : `تحديد الكل (${filtered.length})`}
              </span>
              {selectedIds.size > 0 && (
                <button onClick={() => setSelectedIds(new Set())} className="ms-auto text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1">
                  <SafeIcon icon={LuX} className="w-3 h-3" /> مسح التحديد
                </button>
              )}
            </div>

            {/* Cars list */}
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-50">
              {loading ? (
                <div className="p-8 text-center text-slate-400 text-sm">جاري التحميل...</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">لا توجد نتائج</div>
              ) : (
                filtered.map(car => {
                  const isSelected = selectedIds.has(car.id);
                  const previewCar = preview?.find(p => p.id === car.id);
                  return (
                    <div key={car.id} onClick={() => toggle(car.id)} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${isSelected ? 'bg-violet-50' : 'hover:bg-slate-50'}`}>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-violet-600 border-violet-600' : 'border-slate-300'}`}>
                        {isSelected && <SafeIcon icon={LuCheck} className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-slate-800 truncate">{car.brand} {car.model} <span className="text-slate-400 font-normal text-xs">{car.year}</span></p>
                        <div className="flex gap-3 text-xs text-slate-400 mt-0.5">
                          <span className={field === 'commission' ? 'text-pink-600 font-bold' : ''}>ع.مباشر: {Number(car.commission || 0).toLocaleString()}</span>
                          <span className={field === 'parent_commission' ? 'text-purple-600 font-bold' : ''}>ع.أب: {Number(car.parent_commission || 0).toLocaleString()}</span>
                          <span>شحن: {Number(car.shipping_price || 0).toLocaleString()}</span>
                          <span>إجمالي: {Number(car.total_price || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      {previewCar && amount && (
                        <div className="text-right flex-shrink-0">
                          <p className="text-[10px] text-slate-400 line-through">{Number(car[field] || 0).toLocaleString()}</p>
                          <p className={`text-xs font-black ${selectedField?.color}`}>{previewCar.newVal.toLocaleString()} دج</p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Preview summary */}
        {preview && preview.length > 0 && amount && (
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={LuTrendingUp} className="w-4 h-4 text-violet-600" />
              <p className="text-sm font-black text-violet-700">معاينة التغييرات ({preview.length} سيارة)</p>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {preview.slice(0, 5).map(car => (
                <div key={car.id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 font-bold truncate max-w-[150px]">{car.brand} {car.model}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 line-through">{Number(car[field] || 0).toLocaleString()}</span>
                    <span className="text-violet-700 font-black">← {car.newVal.toLocaleString()} دج</span>
                    {updateTotal && field !== 'commission' && field !== 'parent_commission' && (
                      <span className="text-emerald-600 font-bold text-[10px]">إجمالي: {car.newTotal.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              ))}
              {preview.length > 5 && (
                <p className="text-xs text-violet-500 font-bold text-center">... و {preview.length - 5} سيارة أخرى</p>
              )}
            </div>
          </div>
        )}

        {/* Apply button */}
        <button
          onClick={handleApply}
          disabled={saving || selectedIds.size === 0 || !amount}
          className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-black text-sm transition-all ${done ? 'bg-emerald-500 text-white' : selectedIds.size === 0 || !amount ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg active:scale-[0.98]'}`}
        >
          {saving ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> جاري التطبيق...</>
          ) : done ? (
            <><SafeIcon icon={LuCheck} className="w-4 h-4" /> تم التحديث بنجاح!</>
          ) : (
            <><SafeIcon icon={LuZap} className="w-4 h-4" /> تطبيق على {selectedIds.size} سيارة</>
          )}
        </button>
        {(selectedIds.size === 0 || !amount) && (
          <p className="text-center text-xs text-slate-400">
            {!amount ? 'أدخل المبلغ أولاً' : 'حدد سيارة واحدة على الأقل'}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminBulkPriceUpdate;